<template>
  <div class="container">
    <BaseTitle v-if="title">{{ title }}</BaseTitle>
    <template v-if="brands.length">
      <div class="brands-grid">
        <BrandItem
          v-for="brand in brands"
          :key="brand.id"
          :brand="brand"
          class="brand-item"
        />
      </div>
      <CenteredContent v-if="showLoadMoreButton" class="mt-3 mb-3">
        <BaseButton @click="loadMore">{{ $t("brands.load-more") }}</BaseButton>
      </CenteredContent>
    </template>
    <BaseText v-else-if="input?.searchFilter" class="centered-block">
      {{ $t("search.no-results") }}
    </BaseText>
  </div>
</template>

<script setup lang="ts">
import {
  type BrandsInput,
  type PaginationInput,
  useBrandsQuery,
} from "~~/graphql/generated";

const props = defineProps<{
  title?: string;
  input?: BrandsInput;
  pagination: PaginationInput;
  showLoadMore?: boolean;
}>();

// Mark this as reactive, so the query will re-run when the value changes
const brandsInput = toReactive(props);
const pagination = toReactive(props.pagination);

const variables = computed(() => {
  return {
    brandsInput: {
      ...brandsInput.input,
    },
    pagination,
    limitPlusOffset: pagination.limit + pagination.offset,
  };
});

const { data } = await useBrandsQuery({
  variables,
});

const brands = ref(data.value?.brands ?? []);

const showLoadMoreButton = computed(
  () =>
    props.showLoadMore &&
    data.value?.nextBrand &&
    data.value.nextBrand.length > 0
);

// When the data changes, check if we need to append or replace the data (in case of the load more button press)
watch(data, () => {
  if (pagination.offset === 0) {
    brands.value = data?.value?.brands ?? [];
  } else {
    brands.value = brands.value.concat(data?.value?.brands ?? []);
  }
});

// Watch the input and reset the pagination when it changes
watch(
  () => props.input,
  () => {
    pagination.offset = 0;
  },
  { immediate: true }
);

const loadMore = () => {
  pagination.offset = brands.value.length;
};
</script>

<style lang="scss" scoped>
.brands-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(225px, 1fr));
  @include for-extra-small-screen {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
